import { useEffect, useRef, useCallback } from 'react'
import { useSellerQueueContext } from '../contexts/SellerQueueContext'
import { getSocketIOExports, checkSocketIOCompatibility } from '../utils/socket-compatibility'
import { useSeller } from './useSeller'

export interface SellerQueueRealtimeEvent {
  type: 'position-updated' | 'status-changed' | 'called'
  data: any
  timestamp: Date
}

/**
 * Hook: useSellerQueueRealtime
 * Manages WebSocket connection for real-time seller queue updates
 * Uses existing socket-compatibility layer to avoid breaking existing functionality
 * Handles fallback to polling if websocket unavailable
 */
export const useSellerQueueRealtime = (onEvent?: (event: SellerQueueRealtimeEvent) => void) => {
  const socketRef = useRef<any>(null)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const { setIsOnline } = useSellerQueueContext()
  const { getTokenSeller } = useSeller()

  const tokenSeller = getTokenSeller()
  const sellerId = tokenSeller?.id
  const pluginspaceId = tokenSeller?.pluginspaceId
  const token = tokenSeller ? `${tokenSeller.id}:${tokenSeller.pluginspaceId}` : null

  /**
   * Initialize WebSocket connection
   */
  const initializeSocket = useCallback(() => {
    // Check compatibility first
    const compat = checkSocketIOCompatibility()
    if (!compat.compatible) {
      console.warn('[useSellerQueueRealtime] Socket.io not compatible, using polling fallback')
      setIsOnline(false)
      return false
    }

    try {
      const { io } = getSocketIOExports()
      const SERVICE_URL = process.env.REACT_APP_QUEUE_SERVICE_URL || 'http://localhost:7002'

      socketRef.current = io(SERVICE_URL, {
        auth: {
          token,
          sellerId,
          pluginspaceId,
        },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        transports: ['websocket', 'polling'],
      })

      // Connection established
      socketRef.current.on('connect', () => {
        console.log('[useSellerQueueRealtime] Connected to queue service')
        setIsOnline(true)

        // Register listeners AFTER connection
        socketRef.current.on('seller-queue:called', (data: any) => {
          console.log('[useSellerQueueRealtime] seller-queue:called', data)
          const event: SellerQueueRealtimeEvent = {
            type: 'called',
            data,
            timestamp: new Date(),
          }
          onEvent?.(event)
        })

        socketRef.current.on('seller-queue:position-updated', (data: any) => {
          console.log('[useSellerQueueRealtime] seller-queue:position-updated', data)
          const event: SellerQueueRealtimeEvent = {
            type: 'position-updated',
            data,
            timestamp: new Date(),
          }
          onEvent?.(event)
        })

        socketRef.current.on('seller-queue:status-changed', (data: any) => {
          console.log('[useSellerQueueRealtime] seller-queue:status-changed', data)
          const event: SellerQueueRealtimeEvent = {
            type: 'status-changed',
            data,
            timestamp: new Date(),
          }
          onEvent?.(event)
        })
      })

      // Connection error
      socketRef.current.on('connect_error', (error: any) => {
        console.warn('[useSellerQueueRealtime] Connection error:', error)
        setIsOnline(false)
      })

      // Disconnected
      socketRef.current.on('disconnect', (reason: string) => {
        console.log('[useSellerQueueRealtime] Disconnected:', reason)
        setIsOnline(false)
      })

      return true
    } catch (error) {
      console.error('[useSellerQueueRealtime] Failed to initialize socket:', error)
      setIsOnline(false)
      return false
    }
  }, [token, sellerId, pluginspaceId, setIsOnline, onEvent])

  /**
   * Cleanup socket connection
   */
  const cleanup = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.disconnect()
      socketRef.current = null
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }, [])

  /**
   * Initialize on mount
   */
  useEffect(() => {
    if (!sellerId || !pluginspaceId) return

    const socketConnected = initializeSocket()

    // If socket failed, setup polling fallback every 30 seconds
    if (!socketConnected && !pollIntervalRef.current) {
      console.log('[useSellerQueueRealtime] Setting up polling fallback (30s interval)')
      pollIntervalRef.current = setInterval(() => {
        // This is where we'd emit a manual check event
        // Components that need updates will use refetch functions
      }, 30000)
    }

    return () => cleanup()
  }, [sellerId, pluginspaceId, initializeSocket, cleanup])

  return {
    isConnected: true,
    disconnect: cleanup,
  }
}
